import { useCallback } from 'react'

import { message } from 'antd'

import { FileUploadResult } from './useAttachmentUpload'

import { useUser } from '@/context/userContext'
import { ACTION_STATUSES_TRIGGER_VALIDATION } from '@/modules/actions/constants'

import {
  PlatformAction,
  PlatformActionEntity,
  PlatformActionResourceLink,
  PlatformActionStatus,
  PlatformActionWorker,
} from 'types/api.graphql'

type ActionForValidation = {
  __typename?:
    | PlatformAction['__typename']
    | PlatformActionResourceLink['__typename']
    | PlatformActionEntity['__typename']
    | PlatformActionWorker['__typename']
  isOwner?: boolean
  attachments?: Pick<PlatformAction['attachments'][number], 'id'>[]
  config?: {
    requireAttachmentToClose?: boolean
  }
  status: PlatformActionStatus
}

/**
 * Ensures the following conditions are met:
 * 1. A subcontractor or worker MUST provide an attachment to close an action (if requested)
 * 2. An admin user that is not the creator of the action MUST provide an attachment to close an action (if requested)
 * 3. Any admin user can review the action if it is in WaitingReview without providing an attachment
 */
export const useValidateActionAttachments = () => {
  const { adminUser } = useUser() ?? {}

  const isAdminUser = !!adminUser?.id

  const validateAttachmentRequired = useCallback(
    (
      newStatus: PlatformActionStatus,
      newAttachments: FileUploadResult[],
      existingAction?: ActionForValidation
    ) => {
      const requireAttachments =
        existingAction?.config?.requireAttachmentToClose

      if (!requireAttachments) return true // no validation required

      const isOwner =
        existingAction.__typename === 'PlatformAction'
          ? existingAction.isOwner
          : false

      if (isOwner) return true // owner can close actions without attachments

      const isCurrentlyInReview =
        existingAction?.status === PlatformActionStatus.WaitingReview

      if (isAdminUser && isCurrentlyInReview) return true // admin can close actions in review without attachments

      if (ACTION_STATUSES_TRIGGER_VALIDATION.includes(newStatus)) {
        const hasAttachments =
          existingAction?.attachments?.length > 0 || newAttachments?.length > 0

        if (!hasAttachments) {
          let statusText = 'closed'

          if (newStatus === PlatformActionStatus.Complete) {
            statusText = 'completed'
          } else if (newStatus === PlatformActionStatus.WaitingReview) {
            statusText = 'for review'
          } else if (newStatus === PlatformActionStatus.CantDo) {
            statusText = "'can't do'"
          }

          message.error(
            `You need to upload a new file in the attachments box to set this status as ${statusText}`
          )
          return false
        }
      }

      return true
    },
    [isAdminUser]
  )

  return { validateAttachmentRequired }
}
